<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, addItem, removeItem, updateItem } = useInlineBlockEditor(props.id);

const title = field<string>("title", "");
const items = computed<any[]>(() => (Array.isArray(block.value?.items) ? block.value.items : []));

const addLegendItem = () => addItem("items", { label: "", color: "", value: "" });
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:list" accent="reporting">
    <EditorField :label="t('legendTitleLabel')">
      <Input v-model="title" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <div class="space-y-2">
      <div class="flex items-center justify-between gap-2">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{{ t("itemsLabel") }}</p>
        <Button variant="outline" size="sm" class="h-8 rounded-sm text-[13px]" @click="addLegendItem">
          <Icon mode="svg" size="15" name="lucide:plus" class="mr-1.5" />
          {{ t("addItem") }}
        </Button>
      </div>

      <div v-for="(item, index) in items" :key="`${item.label}-${index}`" class="space-y-2 rounded-md border bg-card p-2">
        <Input :model-value="item.label" class="h-8 rounded-sm bg-muted text-[13px]" :placeholder="t('labelPlaceholder')" @update:model-value="(value) => updateItem('items', index, { label: value })" />
        <div class="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input :model-value="item.value" class="h-8 rounded-sm bg-muted text-[13px]" :placeholder="t('valuePlaceholder')" @update:model-value="(value) => updateItem('items', index, { value })" />
          <EditorThemeColourInput :model-value="item.color" output="css" placeholder="Theme colour or CSS value" @update:model-value="(value) => updateItem('items', index, { color: value })" />
          <Button variant="ghost" size="icon" class="h-8 w-8 rounded-sm text-destructive" @click="removeItem('items', index)">
            <Icon mode="svg" size="15" name="lucide:trash-2" />
          </Button>
        </div>
      </div>
    </div>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Bullet Legend"
  legendTitleLabel: "Title"
  itemsLabel: "Legend Items"
  addItem: "Add Item"
  labelPlaceholder: "Label"
  valuePlaceholder: "Value"
</i18n>
